import React from 'react';

const BreakdownCard = ({ title, data }) => {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  return (
    <div className="card" style={{ flex: 1, minWidth: '180px' }}>
      <h4 style={{ marginBottom: '0.75rem', fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {title}
      </h4>
      {entries.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>—</p>
      ) : (
        entries.map(([key, count]) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', borderBottom: '1px solid var(--border-color)', fontSize: '0.875rem' }}>
            <span>{key}</span>
            <strong>{count}</strong>
          </div>
        ))
      )}
    </div>
  );
};

const SectionLabel = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem', marginTop: '1.5rem' }}>
    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{children}</span>
    <div style={{ flex: 1, height: 1, background: '#f0f0f0' }} />
  </div>
);

const AnalyticsTab = ({ analytics, analyticsLoading }) => {
  if (analyticsLoading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  if (!analytics) {
    return <div className="empty-state"><p>Analytics data not available yet.</p></div>;
  }

  const { player_count, by_age_category, by_event_category, by_event_type, by_status, match_stats, revenue } = analytics;

  const hasRevenue = parseFloat(revenue?.entry_fee) > 0;
  const byEvent = revenue?.by_event || [];

  const summaryCards = [
    { label: 'Players', value: player_count },
    { label: 'Matches Done', value: `${match_stats.completed}/${match_stats.total}` },
    { label: 'Complete', value: `${match_stats.completion_pct}%` },
  ];
  if (hasRevenue) {
    summaryCards.push({ label: 'Revenue', value: `₹${parseFloat(revenue.confirmed).toLocaleString()}` });
  }

  // Registration status breakdown
  const pendingCount = by_status?.pending || 0;
  const waitlistedCount = by_status?.waitlisted || 0;

  return (
    <div>
      {/* Summary cards */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {summaryCards.map(({ label, value }) => (
          <div key={label} className="card" style={{ flex: 1, textAlign: 'center', minWidth: '120px', padding: '1.25rem 0.75rem' }}>
            <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--primary-color)' }}>{value}</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '0.25rem' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Pending / waitlisted alerts */}
      {(pendingCount > 0 || waitlistedCount > 0) && (
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          {pendingCount > 0 && (
            <div style={{ flex: 1, minWidth: 160, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.1rem' }}>⏳</span>
              <div>
                <div style={{ fontWeight: 700, color: '#92400e', fontSize: '1rem' }}>{pendingCount}</div>
                <div style={{ fontSize: '0.78rem', color: '#b45309' }}>Pending approval</div>
              </div>
            </div>
          )}
          {waitlistedCount > 0 && (
            <div style={{ flex: 1, minWidth: 160, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '1.1rem' }}>📋</span>
              <div>
                <div style={{ fontWeight: 700, color: '#1e40af', fontSize: '1rem' }}>{waitlistedCount}</div>
                <div style={{ fontSize: '0.78rem', color: '#1d4ed8' }}>On waitlist</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Player breakdowns */}
      <SectionLabel>Player Breakdown</SectionLabel>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <BreakdownCard title="By Age Category" data={by_age_category} />
        <BreakdownCard title="By Event Category" data={by_event_category} />
        <BreakdownCard title="By Event Type" data={by_event_type} />
      </div>

      {/* Revenue dashboard */}
      {hasRevenue && (
        <>
          <SectionLabel>Revenue Dashboard</SectionLabel>
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <div className="card" style={{ flex: 1, minWidth: 150, textAlign: 'center', padding: '1rem 0.75rem' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#16a34a' }}>₹{parseFloat(revenue.confirmed).toLocaleString()}</div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>Collected</div>
            </div>
            {parseFloat(revenue.pending) > 0 && (
              <div className="card" style={{ flex: 1, minWidth: 150, textAlign: 'center', padding: '1rem 0.75rem' }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#d97706' }}>₹{parseFloat(revenue.pending).toLocaleString()}</div>
                <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>Pending</div>
              </div>
            )}
            <div className="card" style={{ flex: 1, minWidth: 150, textAlign: 'center', padding: '1rem 0.75rem' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#374151' }}>₹{parseFloat(revenue.total).toLocaleString()}</div>
              <div style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: 2 }}>Total Projected</div>
            </div>
          </div>

          {byEvent.length > 0 && (
            <div className="card">
              <h4 style={{ marginBottom: '0.75rem', fontSize: '0.8rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                By Event
              </h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <th style={{ textAlign: 'left', padding: '0.4rem 0.5rem', color: '#6b7280', fontWeight: 600 }}>Event</th>
                    <th style={{ textAlign: 'center', padding: '0.4rem 0.5rem', color: '#6b7280', fontWeight: 600 }}>Fee</th>
                    <th style={{ textAlign: 'center', padding: '0.4rem 0.5rem', color: '#6b7280', fontWeight: 600 }}>Confirmed</th>
                    {byEvent.some(e => e.pending > 0) && <th style={{ textAlign: 'center', padding: '0.4rem 0.5rem', color: '#6b7280', fontWeight: 600 }}>Pending</th>}
                    {byEvent.some(e => e.waitlisted > 0) && <th style={{ textAlign: 'center', padding: '0.4rem 0.5rem', color: '#6b7280', fontWeight: 600 }}>Waitlisted</th>}
                    <th style={{ textAlign: 'right', padding: '0.4rem 0.5rem', color: '#6b7280', fontWeight: 600 }}>Collected</th>
                  </tr>
                </thead>
                <tbody>
                  {byEvent.map(ev => (
                    <tr key={ev.name} style={{ borderBottom: '1px solid #f9fafb' }}>
                      <td style={{ padding: '0.5rem 0.5rem', fontWeight: 500 }}>{ev.name}</td>
                      <td style={{ textAlign: 'center', padding: '0.5rem', color: '#6b7280' }}>₹{ev.entry_fee}</td>
                      <td style={{ textAlign: 'center', padding: '0.5rem', color: '#16a34a', fontWeight: 600 }}>{ev.confirmed}</td>
                      {byEvent.some(e => e.pending > 0) && <td style={{ textAlign: 'center', padding: '0.5rem', color: '#d97706' }}>{ev.pending || '—'}</td>}
                      {byEvent.some(e => e.waitlisted > 0) && <td style={{ textAlign: 'center', padding: '0.5rem', color: '#1d4ed8' }}>{ev.waitlisted || '—'}</td>}
                      <td style={{ textAlign: 'right', padding: '0.5rem', fontWeight: 600 }}>₹{parseFloat(ev.collected).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AnalyticsTab;
